import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  type Auth,
} from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

/**
 * Firebase client SDK bootstrap for AVBP portal.
 *
 * Project: aayatan-veda-hr (shared with website + HR per 2026-04-18 lock)
 * These config values are PUBLIC — they're Firebase "web app identifiers",
 * not secrets. Secrets (BREVO/AISENSY) stay in Cloud Functions env only.
 *
 * Sprint 2 auth model:
 *   1. App boot signs in anonymously → request.auth.uid present in rules.
 *   2. PIN entry resolves which team/{userId} doc applies → role bound to
 *      session via sessionStorage (per dispatch §1, §2).
 *   3. Sprint 3 will swap (1) for a callable Cloud Function that returns a
 *      custom-claim token, so rules can hard-check role server-side.
 *
 * IMPORTANT: appId placeholder must be replaced with the real value from
 * the Firebase Console before any auth-dependent flow can succeed.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyCYfMyN0JX-kLXj9eLDJcR2IiOKH0kUzYk',
  authDomain: 'aayatan-veda-hr.firebaseapp.com',
  projectId: 'aayatan-veda-hr',
  storageBucket: 'aayatan-veda-hr.firebasestorage.app',
  messagingSenderId: '931110839645',
  appId: '1:931110839645:web:3d49fd9415ccd1168434b4',
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);

/**
 * Promise that resolves once an anonymous Firebase Auth session is alive.
 * Call this before any privileged Firestore op. Idempotent.
 */
let _readyPromise: Promise<void> | null = null;
export function ensureAnonAuth(): Promise<void> {
  if (_readyPromise) return _readyPromise;
  _readyPromise = new Promise<void>((resolve, reject) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsub();
        resolve();
        return;
      }
      // Only sign in anonymously when truly unauthenticated — never overwrite
      // a real team or client session that is still restoring from localStorage.
      signInAnonymously(auth).catch((err) => {
        console.warn('Anonymous sign-in failed:', err.message);
        unsub();
        reject(err);
      });
    });
  });
  return _readyPromise;
}
